import { makeAutoObservable, runInAction } from "mobx";
import { IChatHubResponse } from "models/dto/ZoomiLxp/Models/LLM/IChatHubResponse";
import { IChatHubRequest } from "models/dto/ZoomiLxp/Models/LLM/IChatHubRequest";
import { InputSources } from "models/dto/ZoomiLxp/Utilities/Enumerations/InputSources";
import * as signalR from "@microsoft/signalr";
import { AppStore } from "store";
import { SiteSettingsKeys } from "models/dto/ZoomiLxp/Utilities/Constants/SiteSettingsKeys";
import { ChatBotHubMethods } from "models/dto/ZoomiLxp/Utilities/Constants/ChatBotHubMethods";
import { RequestOrigins } from "models/dto/ZoomiLxp/Utilities/Enumerations/RequestOrigins";

export type ChatMessage = {
	id: number | string;
	author: string;
	text: string;
	time: string;
	from: "user" | "bot";
};

export class ChatBotStore {
	messages: ChatMessage[] = [];
	isLoading = false;
	error: string | null = null;
	currentCourseId?: number = undefined;
	currentContentId?: number = undefined;
	currentOrigin?: RequestOrigins = undefined;
	isStreaming = false;

	private isSubscribed = false;
	private currentBotMessageId: string | null = null;
	private rootStore;

	constructor(rootStore: AppStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
	}

	get aiTutorName(): string {
		return this.rootStore.settingsStore.publicSettings.AITutor?.[SiteSettingsKeys.AITutorName] ?? "AI Tutor";
	}

	get placeholderMessage(): ChatMessage {
		const timeNow = new Date();
		return {
			id: `user-${timeNow.getTime()}`,
			from: "bot",
			author: this.aiTutorName,
			text: "Ask me anything",
			time: timeNow.toUTCString(),
		};
	}

	async loadHistory(origin: RequestOrigins, userId?: number, courseId?: number, contentId?: number) {
		this.isLoading = true;
		this.error = null;
		this.messages = [];

		try {
			if (!userId) {
				runInAction(() => {
					this.isLoading = false;
				});
				return;
			}

			if (!this.canProcessRequest(origin, courseId)) {
				runInAction(() => {
					this.isLoading = false;
				});
				return;
			}

			await this.initSignalR(origin, courseId, contentId);

			const request: IChatHubRequest = {
				prompt: "",
				origin,
				...(courseId !== undefined ? { courseId } : {}),
				...(contentId !== undefined ? { contentId } : {}),
			};

			const connection = this.rootStore.signalRStore.connection;

			if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
				throw new Error("SignalR connection is not established.");
			}

			await connection.invoke(ChatBotHubMethods.ReadHistoryStream, request);

			runInAction(() => {
				this.isLoading = false;
			});
		} catch (e) {
			runInAction(() => {
				this.error = (e as Error).message;
				this.isLoading = false;
			});
		}
	}

	sendMessage(
		text: string,
		origin: RequestOrigins,
		userId?: number,
		courseId?: number,
		contentId?: number
	) {
		const trimmed = text.trim();
		if (!trimmed) return;

		if (!this.canProcessRequest(origin, courseId)) {
			return;
		}

		(async () => {
			const timeNow = new Date();

			const userMessage: ChatMessage = {
				id: `user-${timeNow.getTime()}`,
				from: "user",
				author: "You",
				text: trimmed,
				time: timeNow.toUTCString(),
			};

			this.messages.push(userMessage);

			const connection = this.rootStore.signalRStore.connection;

			if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
				console.log("Connection Error: " + this.error);
				return;
			}

			this.isStreaming = true;
			this.currentBotMessageId = `bot-stream-${timeNow.getTime()}`;

			const streamingMessage: ChatMessage = {
				id: this.currentBotMessageId,
				from: "bot",
				author: this.aiTutorName,
				text: "",
				time: timeNow.toUTCString(),
			};
			this.messages.push(streamingMessage);

			const request: IChatHubRequest = {
				prompt: trimmed,
				origin,
				...(courseId !== undefined ? { courseId } : {}),
				...(contentId !== undefined ? { contentId } : {}),
			};

			try {
				await connection.invoke(ChatBotHubMethods.StartChatStream, request);
			} catch (e) {
				runInAction(() => {
					this.error = (e as Error).message;
					this.isStreaming = false;
					this.currentBotMessageId = null;
				});
			}
		})();
	}

	async initSignalR(origin: RequestOrigins, courseId?: number, contentId?: number) {
		this.currentCourseId = courseId;
		this.currentContentId = contentId;
		this.currentOrigin = origin;

		// One connection per app, owned by SignalRStore. Building a second one here meant
		// server pushes for other features (Clients.User fans out to every connection of the
		// user) landed on a socket with no handler for them and were dropped without a trace.
		try {
			await this.rootStore.signalRStore.connect();
		} catch (e) {
			runInAction(() => {
				this.error = (e as Error).message;
			});
			return;
		}

		this.subscribeToSignalR();
	}

	async disposeSignalR() {
		this.currentCourseId = undefined;
		this.currentContentId = undefined;
		this.currentOrigin = undefined;

		this.unsubscribeFromSignalR();
	}

	private subscribeToSignalR() {
		if (this.isSubscribed) {
			return;
		}

		const connection = this.rootStore.signalRStore.connection;

		if (!connection) {
			return;
		}

		connection.on(ChatBotHubMethods.HistoryData, (response) => {
			this.handleIncomingMessageFromServer(response);
		});

		connection.on(ChatBotHubMethods.ReceiveDelta, (response) => {
			this.handleReceiveDelta(response);
		});

		connection.on(ChatBotHubMethods.ReceiveError, (err) => {
			runInAction(() => {
				this.error = err;
				this.isStreaming = false;
				this.currentBotMessageId = null;
			});
		});

		connection.on(ChatBotHubMethods.StreamCompleted, () => {
			runInAction(() => {
				this.isStreaming = false;
				this.currentBotMessageId = null;
			});
		});

		this.isSubscribed = true;
	}

	private unsubscribeFromSignalR() {
		const connection = this.rootStore.signalRStore.connection;

		// Only the chat's own handlers come off. The connection is shared and must stay up:
		// stopping it here would take the app's other realtime consumers down with the chat.
		if (connection) {
			connection.off(ChatBotHubMethods.HistoryData);
			connection.off(ChatBotHubMethods.ReceiveDelta);
			connection.off(ChatBotHubMethods.ReceiveError);
			connection.off(ChatBotHubMethods.StreamCompleted);
		}

		runInAction(() => {
			this.isSubscribed = false;
			this.isStreaming = false;
			this.currentBotMessageId = null;
		});
	}

	isMessageStreaming(messageId: ChatMessage["id"]) {
		return this.isStreaming && this.currentBotMessageId === messageId;
	}

	handleIncomingMessageFromServer(response: IChatHubResponse) {
		const inputSource = response.inputSource ?? InputSources.None;

		const isUser = inputSource === InputSources.User;
		const from: "user" | "bot" = isUser ? "user" : "bot";
		const author = isUser ? "You" : this.aiTutorName;

		const message: ChatMessage = {
			id: response.id,
			from,
			author,
			text: response.text,
			time: response.dateTimeStamp,
		};

		this.messages.push(message);
	}

	private handleReceiveDelta(response: IChatHubResponse) {
		const inputSource = response.inputSource ?? InputSources.None;

		if (inputSource !== InputSources.ChatGPT && inputSource !== InputSources.Bedrock) {
			return;
		}

		if (!response.text) return;
		if (!this.currentBotMessageId) {
			return;
		}

		const idx = this.messages.findIndex((m) => m.id === this.currentBotMessageId);
		if (idx === -1) {
			return;
		}

		this.messages[idx] = {
			...this.messages[idx],
			text: this.messages[idx].text + response.text,
		};
	}

	private canProcessRequest(origin: RequestOrigins, courseId?: number) {
		return origin !== RequestOrigins.MyDesk || courseId !== undefined;
	}
}
